/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Dialog, DialogContent, Divider } from "@mui/material";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getTariffs, getTariffsProperties, getBillConfigById } from "../../lib/Queries";
import { getTimezoneAbbreviation } from "../../lib/Util";
import TariffCard from "../Tariff/TariffCard";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * ReportDetailsModal displays the bill calculation configuration details
 * for a performance report, including tariff cards for baseline and actual configs.
 *
 * Configs referenced by the report may have been deactivated (superseded), so
 * they might not be present in the active billConfigs list. When that happens
 * the modal fetches them individually by ID.
 */
const ReportDetailsModal = ({ open, onClose, report, billConfigs = [], siteUuid, siteTimezone }) => {
  const [tariffData, setTariffData] = useState({ pre: null, post: null });
  const [tariffPropertiesCounts, setTariffPropertiesCounts] = useState({ pre: 0, post: 0 });
  const [propertiesDisplayNames, setPropertiesDisplayNames] = useState({ pre: {}, post: {} });
  const [isLoadingTariffs, setIsLoadingTariffs] = useState(false);
  const [fetchedConfigs, setFetchedConfigs] = useState({});

  // Fetch any config IDs that aren't in the active billConfigs list
  useEffect(() => {
    if (!open || !siteUuid || !report) return;

    const idsToFetch = [report.baseline_config_id, report.actual_config_id].filter(
      (id) => id != null && !billConfigs.some((c) => c.config_id === id),
    );

    if (idsToFetch.length === 0) return;

    let cancelled = false;
    const fetchMissing = async () => {
      const results = await Promise.all(
        idsToFetch.map((id) =>
          getBillConfigById(siteUuid, id).catch(() => null),
        ),
      );
      if (cancelled) return;
      const map = {};
      results.forEach((cfg) => {
        if (cfg) map[cfg.config_id] = cfg;
      });
      setFetchedConfigs((prev) => ({ ...prev, ...map }));
    };
    fetchMissing();
    return () => { cancelled = true; };
  }, [open, siteUuid, report, billConfigs]);

  const findConfig = (configId) => {
    if (configId == null) return null;
    return billConfigs.find((c) => c.config_id === configId) || fetchedConfigs[configId] || null;
  };

  const baselineConfig = findConfig(report?.baseline_config_id);
  const actualConfig = findConfig(report?.actual_config_id);

  const hasBothConfigs = !!baselineConfig && !!actualConfig;
  const isSingleConfig =
    (!!baselineConfig && !actualConfig) ||
    (!baselineConfig && !!actualConfig) ||
    (!!baselineConfig && !!actualConfig && baselineConfig.config_id === actualConfig.config_id);

  useEffect(() => {
    const fetchTariffDetails = async () => {
      if (!open) return;
      if (!baselineConfig && !actualConfig) return;

      setIsLoadingTariffs(true);
      try {
        const promises = [];
        const propertiesPromises = [];

        if (baselineConfig?.master_tariff_id) {
          promises.push(
            getTariffs(String(baselineConfig.master_tariff_id))
              .then((data) => ({ type: "pre", data }))
              .catch(() => ({ type: "pre", data: null })),
          );
          propertiesPromises.push(
            getTariffsProperties(String(baselineConfig.master_tariff_id))
              .then((data) => ({ type: "pre", data }))
              .catch(() => ({ type: "pre", data: null })),
          );
        }

        if (actualConfig?.master_tariff_id && actualConfig.config_id !== baselineConfig?.config_id) {
          promises.push(
            getTariffs(String(actualConfig.master_tariff_id))
              .then((data) => ({ type: "post", data }))
              .catch(() => ({ type: "post", data: null })),
          );
          propertiesPromises.push(
            getTariffsProperties(String(actualConfig.master_tariff_id))
              .then((data) => ({ type: "post", data }))
              .catch(() => ({ type: "post", data: null })),
          );
        }

        const [tariffResults, propertiesResults] = await Promise.all([
          Promise.all(promises),
          Promise.all(propertiesPromises),
        ]);

        const newTariffData = { pre: null, post: null };
        const newPropertiesCounts = { pre: 0, post: 0 };
        const newDisplayNames = { pre: {}, post: {} };

        tariffResults.forEach((result) => {
          if (result.data?.results?.length > 0) {
            newTariffData[result.type] = result.data.results[0];
          }
        });

        propertiesResults.forEach((result) => {
          if (result.data?.results) {
            newPropertiesCounts[result.type] = result.data.count || result.data.results.length;
            const displayMap = {};
            result.data.results.forEach((prop) => {
              displayMap[prop.keyName] = prop.displayName || prop.keyName;
            });
            newDisplayNames[result.type] = displayMap;
          }
        });

        setTariffData(newTariffData);
        setTariffPropertiesCounts(newPropertiesCounts);
        setPropertiesDisplayNames(newDisplayNames);
      } catch (error) {
        console.error("Error fetching tariff details:", error);
      } finally {
        setIsLoadingTariffs(false);
      }
    };

    fetchTariffDetails();
  }, [open, baselineConfig, actualConfig]);

  // Parse calculated_at as UTC (backend stores UTC without tz info)
  const calculatedAt = report?.calculated_at
    ? dayjs.utc(report.calculated_at).local().format("M/D/YYYY, h:mm:ss A")
    : "Unknown";
  const calculatedBy = report?.calculated_by || "Canopy";

  // Format billing cycle dates in the site's local timezone
  const tzAbbr = siteTimezone ? getTimezoneAbbreviation(siteTimezone) : null;
  const billingStart = report?.billing_cycle_start && siteTimezone
    ? dayjs.utc(report.billing_cycle_start).tz(siteTimezone).format("MMM D, YYYY")
    : report?.billing_cycle_start
      ? dayjs(report.billing_cycle_start).format("MMM D, YYYY")
      : null;
  const billingEnd = report?.billing_cycle_end && siteTimezone
    ? dayjs.utc(report.billing_cycle_end).tz(siteTimezone).format("MMM D, YYYY")
    : report?.billing_cycle_end
      ? dayjs(report.billing_cycle_end).format("MMM D, YYYY")
      : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { p: 5 } }}>
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Typography variant="h5" sx={{ mb: 0.5 }}>
          Report Details
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Calculated {calculatedAt} by {calculatedBy}
        </Typography>

        {/* Billing Cycle & Calculation Metadata */}
        <Box sx={{ mb: 3 }}>
          {billingStart && billingEnd && (
            <Typography variant="body2" color="text.secondary">
              Billing Cycle: {billingStart} – {billingEnd}{tzAbbr ? ` (${tzAbbr})` : ""}
            </Typography>
          )}
          {(report?.baseline_calculation_id || report?.actual_calculation_id) && (
            <Typography variant="body2" color="text.secondary">
              Calculation IDs:{" "}
              {[
                report?.baseline_calculation_id ? `Baseline #${report.baseline_calculation_id}` : null,
                report?.actual_calculation_id ? `Actual #${report.actual_calculation_id}` : null,
              ]
                .filter(Boolean)
                .join(", ")}
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Tariff Cards */}
        {isSingleConfig ? (
          <TariffCard
            config={baselineConfig || actualConfig}
            label=""
            tariffDetails={tariffData.pre || tariffData.post}
            totalPropertiesCount={tariffPropertiesCounts.pre || tariffPropertiesCounts.post}
            displayNameMap={propertiesDisplayNames.pre || propertiesDisplayNames.post}
            isLoading={isLoadingTariffs}
            showLabel={false}
            hasSiblingCard={false}
          />
        ) : (
          <Box sx={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            {baselineConfig && (
              <TariffCard
                config={baselineConfig}
                label="Pre-Project"
                tariffDetails={tariffData.pre}
                totalPropertiesCount={tariffPropertiesCounts.pre}
                displayNameMap={propertiesDisplayNames.pre}
                isLoading={isLoadingTariffs}
                showLabel={hasBothConfigs}
                hasSiblingCard={hasBothConfigs}
              />
            )}
            {actualConfig && actualConfig.config_id !== baselineConfig?.config_id && (
              <TariffCard
                config={actualConfig}
                label="Post-Project"
                tariffDetails={tariffData.post}
                totalPropertiesCount={tariffPropertiesCounts.post}
                displayNameMap={propertiesDisplayNames.post}
                isLoading={isLoadingTariffs}
                showLabel={hasBothConfigs}
                hasSiblingCard={hasBothConfigs}
              />
            )}
          </Box>
        )}

        {/* Close Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

ReportDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  report: PropTypes.shape({
    calculated_at: PropTypes.string,
    calculated_by: PropTypes.string,
    billing_cycle_start: PropTypes.string,
    billing_cycle_end: PropTypes.string,
    baseline_config_id: PropTypes.number,
    actual_config_id: PropTypes.number,
    baseline_calculation_id: PropTypes.number,
    actual_calculation_id: PropTypes.number,
  }),
  billConfigs: PropTypes.array,
  siteUuid: PropTypes.string,
  siteTimezone: PropTypes.string,
};

export default ReportDetailsModal;
